import React, { useState } from "react";
import { Archive, MessageSquare } from "lucide-react";
import { Breadcrumb } from "@/shared/components/Breadcrumb";
import { SearchInput, PageHeader } from "@/shared/components/ui-kit";
import PixelAvatar from "@/shared/components/PixelAvatar";
import { agentMood } from "@/shared/lib/agent-mood";
// Lives in shared/ because the agent settings version history renders it too.
import { RestoreButton } from "@/shared/components/RestoreButton";
import type { Agent, Chat } from "@/shared/types";

function ChatRow({ chat, onRestore, onOpen }: { chat: Chat; onRestore: () => void; onOpen: () => void }) {
  return (
    <div className="group flex items-center gap-3 px-4 py-2.5 hover:bg-[var(--accent-hover)] transition-colors">
      <MessageSquare className="w-3.5 h-3.5 shrink-0 text-[var(--text-quaternary)]" strokeWidth={1.75} />
      <button onClick={onOpen} className="min-w-0 flex-1 text-left press">
        <div className="flex items-center gap-2">
          <span className="truncate text-[14px] text-[var(--text-primary)] tracking-[-0.005em]">
            {chat.title}
          </span>
        </div>
        <div className="text-[11.5px] text-[var(--text-tertiary)] mt-[1px]">
          Archived {chat.archivedAt || "recently"} · last active {chat.updatedAt}
        </div>
      </button>
      <RestoreButton onClick={onRestore} title="Restore chat" reveal />
    </div>
  );
}

export default function ArchivePage({ agents = [], onRestoreChat, onOpenChat }: {
  agents?: Agent[];
  onRestoreChat: (agentId: string, chatId: string) => void;
  onOpenChat: (agentId: string, chatId: string) => void;
}) {
  const [query, setQuery] = useState("");
  const q = query.toLowerCase();

  const groups = agents
    .map((a) => ({
      agent: a,
      chats: a.chats.filter(
        (c) => c.archived && (!q || c.title.toLowerCase().includes(q) || a.name.toLowerCase().includes(q))
      ),
    }))
    .filter((g) => g.chats.length > 0);

  const total = agents.reduce((n, a) => n + a.chats.filter((c) => c.archived).length, 0);

  return (
    <main className="flex-1 min-w-0 flex flex-col bg-[var(--canvas)]">
      {/* Header */}
      <PageHeader
        title={<Breadcrumb>Archive</Breadcrumb>}
      >
        <SearchInput
          value={query}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
          placeholder="Search archive"
          className="w-[220px]"
        />
      </PageHeader>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[720px] mx-auto px-4 md:px-6 py-8 fade-in-up">
          <div className="flex items-baseline gap-2 mb-5">
            <h1 className="text-[17px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">
              Archived chats
            </h1>
            <span className="text-[12.5px] text-[var(--text-tertiary)] tabular-nums">
              {total} {total === 1 ? "chat" : "chats"}
            </span>
          </div>

          {groups.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-11 h-11 rounded-[12px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] flex items-center justify-center mb-3">
                <Archive className="w-[18px] h-[18px] text-[var(--text-tertiary)]" strokeWidth={1.5} />
              </div>
              <div className="text-[14px] font-medium text-[var(--text-primary)]">
                {q ? "No matches" : "Nothing archived"}
              </div>
              <div className="text-[12.5px] text-[var(--text-tertiary)] mt-1 max-w-[280px]">
                {q
                  ? "No archived chats match your search."
                  : "Chats you archive will appear here, grouped by agent."}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {groups.map(({ agent, chats }) => (
                <section
                  key={agent.id}
                  className="rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] overflow-hidden"
                >
                  <div className="flex items-center gap-2.5 px-4 py-3 border-b hairline">
                    <PixelAvatar seed={agent.seed} hue={agent.hue} emotion={agentMood(agent)} size={20} ring rounded={5} />
                    <span className="flex-1 text-[13px] font-medium text-[var(--text-primary)] tracking-[-0.005em]">
                      {agent.name}
                    </span>
                    <span className="text-[11.5px] text-[var(--text-tertiary)] tabular-nums">
                      {chats.length} {chats.length === 1 ? "chat" : "chats"}
                    </span>
                  </div>
                  <div className="divide-y divide-[var(--hairline)]">
                    {chats.map((c) => (
                      <ChatRow
                        key={c.id}
                        chat={c}
                        onRestore={() => onRestoreChat(agent.id, c.id)}
                        onOpen={() => onOpenChat(agent.id, c.id)}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
